import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  CircularProgress,
  Drawer,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  InputAdornment,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { ru } from 'date-fns/locale';
import { assetsApi, holdersApi } from '../services/api';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  getAssetTypeLabel,
  getStatusLabel,
  getStatusColor,
  getCountryLabel,
  ASSET_TYPES,
} from '../utils/formatters';
import { useApp } from '../App';

const ASSET_TYPE_OPTIONS = Object.keys(ASSET_TYPES);
const STATUS_OPTIONS = ['Free', 'Blocked'];
const COUNTRY_OPTIONS = ['RU', 'US', 'UAE', 'CY', 'Other'];

const Assets = () => {
  const { showNotification, workspace } = useApp();
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [holders, setHolders] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    holders: [],
    types: [],
    status: 'All',
    country: 'All',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [assetsRes, holdersRes] = await Promise.all([
        assetsApi.getAll({
          holder: filters.holders.join(',') || undefined,
          type: filters.types.join(',') || undefined,
          status: filters.status !== 'All' ? filters.status : undefined,
          country: filters.country !== 'All' ? filters.country : undefined,
          search: filters.search || undefined,
        }),
        holdersApi.getAll(),
      ]);

      // Apply workspace filter
      let filteredAssets = assetsRes.data;
      if (workspace === 'ru') {
        filteredAssets = filteredAssets.filter((a) => a.country === 'RU');
      } else if (workspace === 'non-ru') {
        filteredAssets = filteredAssets.filter((a) => a.country !== 'RU');
      }

      setAssets(filteredAssets);
      setHolders(holdersRes.data);
    } catch (error) {
      showNotification('Ошибка загрузки активов', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters, workspace, showNotification]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      holders: [],
      types: [],
      status: 'All',
      country: 'All',
    });
  };

  const handleOpenDialog = (asset = null) => {
    setEditAsset(
      asset || {
        ticker: '',
        name: '',
        type: 'EQUITY',
        currency: 'USD',
        quantity: '',
        price: '',
        costBasis: '',
        status: 'Free',
        country: 'RU',
        holderId: '',
        account: '',
        broker: '',
        purchaseDate: null,
        maturityDate: null,
        interestRate: '',
        notes: '',
      }
    );
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditAsset(null);
  };

  const handleSaveAsset = async () => {
    try {
      const data = {
        ...editAsset,
        quantity: parseFloat(editAsset.quantity) || 0,
        price: parseFloat(editAsset.price) || 0,
        costBasis: parseFloat(editAsset.costBasis) || 0,
        interestRate: editAsset.interestRate ? parseFloat(editAsset.interestRate) : null,
      };

      if (editAsset.id) {
        await assetsApi.update(editAsset.id, data);
        showNotification('Актив обновлен', 'success');
      } else {
        await assetsApi.create(data);
        showNotification('Актив создан', 'success');
      }

      handleCloseDialog();
      fetchData();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Ошибка сохранения', 'error');
    }
  };

  const handleDeleteAsset = async (id) => {
    if (!window.confirm('Удалить актив?')) return;

    try {
      await assetsApi.delete(id);
      showNotification('Актив удален', 'success');
      fetchData();
    } catch (error) {
      showNotification('Ошибка удаления', 'error');
    }
  };

  const handleBlockToggle = async (id, currentStatus) => {
    try {
      const action = currentStatus === 'Free' ? 'block' : 'unblock';
      await assetsApi.blockToggle(id, action);
      showNotification(`Актив ${action === 'block' ? 'заблокирован' : 'разблокирован'}`, 'success');
      fetchData();
    } catch (error) {
      showNotification('Ошибка изменения статуса', 'error');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedAssets.length === 0) {
      showNotification('Выберите активы', 'warning');
      return;
    }

    try {
      await assetsApi.bulkAction(selectedAssets, action);
      showNotification(`Операция выполнена для ${selectedAssets.length} активов`, 'success');
      setSelectedAssets([]);
      fetchData();
    } catch (error) {
      showNotification('Ошибка выполнения операции', 'error');
    }
  };

  const handleRowClick = (params) => {
    setSelectedAsset(params.row);
    setDetailOpen(true);
  };

  const columns = [
    {
      field: 'ticker',
      headerName: 'Тикер',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      ),
    },
    { field: 'name', headerName: 'Наименование', width: 180, flex: 1 },
    {
      field: 'type',
      headerName: 'Тип',
      width: 120,
      valueFormatter: (params) => getAssetTypeLabel(params.value),
    },
    {
      field: 'Holder',
      headerName: 'Владелец',
      width: 100,
      valueGetter: (params) => params.row.Holder?.code || '-',
      renderCell: (params) => <Chip label={params.value} size="small" />,
    },
    {
      field: 'quantity',
      headerName: 'Количество',
      width: 110,
      type: 'number',
      valueFormatter: (params) => params.value?.toLocaleString('ru-RU'),
    },
    {
      field: 'price',
      headerName: 'Цена',
      width: 100,
      type: 'number',
      valueFormatter: (params) => params.value?.toLocaleString('ru-RU', { minimumFractionDigits: 2 }),
    },
    { field: 'currency', headerName: 'Валюта', width: 80 },
    {
      field: 'valueUSD',
      headerName: 'Стоимость (USD)',
      width: 140,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {formatCurrency(params.value, 'USD')}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Статус',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          size="small"
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'country',
      headerName: 'Страна',
      width: 90,
      valueFormatter: (params) => getCountryLabel(params.value),
    },
    {
      field: 'portfolioShare',
      headerName: 'Доля',
      width: 80,
      type: 'number',
      valueFormatter: (params) => `${params.value}%`,
    },
    {
      field: 'actions',
      headerName: 'Действия',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Редактировать">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialog(params.row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.status === 'Free' ? 'Заблокировать' : 'Разблокировать'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleBlockToggle(params.row.id, params.row.status);
              }}
            >
              {params.row.status === 'Free' ? (
                <LockIcon fontSize="small" />
              ) : (
                <LockOpenIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Удалить">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteAsset(params.row.id);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={600}>
            Активы
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />}>
              Экспорт
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Добавить актив
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Поиск по тикеру или названию"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Владельцы</InputLabel>
                  <Select
                    multiple
                    value={filters.holders}
                    onChange={(e) => handleFilterChange('holders', e.target.value)}
                    input={<OutlinedInput label="Владельцы" />}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {holders.map((h) => (
                      <MenuItem key={h.id} value={h.code}>
                        <Checkbox checked={filters.holders.includes(h.code)} />
                        <ListItemText primary={h.code} secondary={h.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Тип актива</InputLabel>
                  <Select
                    multiple
                    value={filters.types}
                    onChange={(e) => handleFilterChange('types', e.target.value)}
                    input={<OutlinedInput label="Тип актива" />}
                    renderValue={(selected) => selected.map(getAssetTypeLabel).join(', ')}
                  >
                    {ASSET_TYPE_OPTIONS.map((type) => (
                      <MenuItem key={type} value={type}>
                        <Checkbox checked={filters.types.includes(type)} />
                        <ListItemText primary={getAssetTypeLabel(type)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Статус</InputLabel>
                  <Select
                    value={filters.status}
                    label="Статус"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="All">Все</MenuItem>
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {getStatusLabel(s)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Страна</InputLabel>
                  <Select
                    value={filters.country}
                    label="Страна"
                    onChange={(e) => handleFilterChange('country', e.target.value)}
                  >
                    <MenuItem value="All">Все</MenuItem>
                    {COUNTRY_OPTIONS.map((c) => (
                      <MenuItem key={c} value={c}>
                        {getCountryLabel(c)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Сбросить фильтры">
                    <IconButton onClick={resetFilters}>
                      <FilterIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Обновить">
                    <IconButton onClick={fetchData}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>

            {selectedAssets.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Выбрано: {selectedAssets.length}
                </Typography>
                <Button size="small" onClick={() => handleBulkAction('block')}>
                  Заблокировать
                </Button>
                <Button size="small" onClick={() => handleBulkAction('unblock')}>
                  Разблокировать
                </Button>
                <Button size="small" color="error" onClick={() => handleBulkAction('delete')}>
                  Удалить
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Data Grid */}
        <Card>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={assets}
              columns={columns}
              loading={loading}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={(ids) => setSelectedAssets(ids)}
              rowSelectionModel={selectedAssets}
              onRowClick={handleRowClick}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pageSizeOptions={[25, 50, 100]}
              sx={{
                '& .MuiDataGrid-row:hover': {
                  cursor: 'pointer',
                },
              }}
            />
          </Box>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{editAsset?.id ? 'Редактировать актив' : 'Добавить актив'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Тикер"
                  value={editAsset?.ticker || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, ticker: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  required
                  label="Наименование"
                  value={editAsset?.name || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Тип актива</InputLabel>
                  <Select
                    value={editAsset?.type || 'EQUITY'}
                    label="Тип актива"
                    onChange={(e) => setEditAsset({ ...editAsset, type: e.target.value })}
                  >
                    {ASSET_TYPE_OPTIONS.map((type) => (
                      <MenuItem key={type} value={type}>
                        {getAssetTypeLabel(type)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Владелец</InputLabel>
                  <Select
                    value={editAsset?.holderId || ''}
                    label="Владелец"
                    onChange={(e) => setEditAsset({ ...editAsset, holderId: e.target.value })}
                  >
                    {holders.map((h) => (
                      <MenuItem key={h.id} value={h.id}>
                        {h.code} - {h.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Статус</InputLabel>
                  <Select
                    value={editAsset?.status || 'Free'}
                    label="Статус"
                    onChange={(e) => setEditAsset({ ...editAsset, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {getStatusLabel(s)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Количество"
                  type="number"
                  value={editAsset?.quantity || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, quantity: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Цена"
                  type="number"
                  value={editAsset?.price || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, price: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Валюта</InputLabel>
                  <Select
                    value={editAsset?.currency || 'USD'}
                    label="Валюта"
                    onChange={(e) => setEditAsset({ ...editAsset, currency: e.target.value })}
                  >
                    {['USD', 'EUR', 'RUB', 'AED', 'GBP', 'CHF'].map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Себестоимость"
                  type="number"
                  value={editAsset?.costBasis || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, costBasis: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Страна</InputLabel>
                  <Select
                    value={editAsset?.country || 'RU'}
                    label="Страна"
                    onChange={(e) => setEditAsset({ ...editAsset, country: e.target.value })}
                  >
                    {COUNTRY_OPTIONS.map((c) => (
                      <MenuItem key={c} value={c}>
                        {getCountryLabel(c)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Брокер"
                  value={editAsset?.broker || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, broker: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Счет"
                  value={editAsset?.account || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, account: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Дата покупки"
                  value={editAsset?.purchaseDate ? new Date(editAsset.purchaseDate) : null}
                  onChange={(date) =>
                    setEditAsset({
                      ...editAsset,
                      purchaseDate: date?.toISOString().split('T')[0],
                    })
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              {['BONDS', 'DEPOSIT'].includes(editAsset?.type) && (
                <>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Дата погашения"
                      value={editAsset?.maturityDate ? new Date(editAsset.maturityDate) : null}
                      onChange={(date) =>
                        setEditAsset({
                          ...editAsset,
                          maturityDate: date?.toISOString().split('T')[0],
                        })
                      }
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Процентная ставка (%)"
                      type="number"
                      value={editAsset?.interestRate || ''}
                      onChange={(e) => setEditAsset({ ...editAsset, interestRate: e.target.value })}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Примечания"
                  value={editAsset?.notes || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button variant="contained" onClick={handleSaveAsset}>
              Сохранить
            </Button>
          </DialogActions>
        </Dialog>

        {/* Detail Drawer */}
        <Drawer
          anchor="right"
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          PaperProps={{ sx: { width: 400 } }}
        >
          {selectedAsset && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{selectedAsset.ticker}</Typography>
                <IconButton onClick={() => setDetailOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {selectedAsset.name}
              </Typography>
              <Chip
                label={getStatusLabel(selectedAsset.status)}
                color={getStatusColor(selectedAsset.status)}
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Тип</TableCell>
                    <TableCell align="right">{getAssetTypeLabel(selectedAsset.type)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Владелец</TableCell>
                    <TableCell align="right">{selectedAsset.Holder?.code}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Количество</TableCell>
                    <TableCell align="right">{selectedAsset.quantity?.toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Цена</TableCell>
                    <TableCell align="right">
                      {selectedAsset.price?.toLocaleString()} {selectedAsset.currency}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Стоимость (USD)</TableCell>
                    <TableCell align="right">{formatCurrency(selectedAsset.valueUSD, 'USD')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Доля в портфеле</TableCell>
                    <TableCell align="right">{formatPercent(selectedAsset.portfolioShare)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Страна</TableCell>
                    <TableCell align="right">{getCountryLabel(selectedAsset.country)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Брокер</TableCell>
                    <TableCell align="right">{selectedAsset.broker || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Дата покупки</TableCell>
                    <TableCell align="right">{formatDate(selectedAsset.purchaseDate)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setDetailOpen(false);
                    handleOpenDialog(selectedAsset);
                  }}
                >
                  Редактировать
                </Button>
              </Box>
            </Box>
          )}
        </Drawer>
      </Box>
    </LocalizationProvider>
  );
};

export default Assets;
